import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useConfirm } from './MailingDialogs';
import { ApiError } from '../api/client';
import { Icon } from '../rondo/marketing/Icon';
import { mailingApi, type CampaignClick, type CampaignDetail, type CampaignJob, type CampaignListItem, type CampaignStatus, type MailingSpace } from '../api/mailing';
import { useTabParam } from '../shell/useTabParam';
import { MailingCampaignWizard, MailingCampaignTypeChoice, type EditorMode } from './MailingCampaignWizard';
import { isPromoTemplate } from './promo-news';
import { sampleVars } from './mailing-content';
import { ServiceBar } from '../shell/ServiceBar';
import { ArcPage, ArcTabs } from '../components/arc/primitives/arc-primitives';
import { Button } from '../components/arc/button/button';

const FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };
const LIMITS = [25, 50, 100, 200, 0];

const STATUS_META: Record<CampaignStatus, { label: string; bg: string; color: string }> = {
  draft:     { label: 'Koncept',     bg: '#eef2f7', color: '#475569' },
  scheduled: { label: 'Naplánováno', bg: '#e0f2fe', color: '#0369a1' },
  running:   { label: 'Odesílá se',  bg: '#fef9c3', color: '#854d0e' },
  paused:    { label: 'Pozastaveno', bg: '#fee2e2', color: '#b91c1c' },
  sent:      { label: 'Odeslaná',    bg: '#ede9fe', color: '#5b3f96' },
  completed: { label: 'Dokončená',   bg: '#ede9fe', color: '#5b3f96' },
  archived:  { label: 'Archivovaná', bg: '#f3f4f6', color: '#6b7280' },
};

const TYPE_META: Record<string, { icon: string; label: string }> = {
  oneshot:   { icon: 'send',   label: 'Jednorázová' },
  automated: { icon: 'zap',    label: 'Automatizovaná' },
  recurring: { icon: 'repeat', label: 'Opakovaná' },
};

const JOB_STATE: Record<string, string> = {
  pending: 'Čeká', sending: 'Odesílá se', sent: 'Odesláno', failed: 'Chyba', skipped: 'Přeskočeno',
};

function campaignInitials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'K';
}

type Tab = 'active' | 'inactive' | 'draft' | 'archived';
const KAMPANE_TABS: readonly Tab[] = ['active', 'inactive', 'draft', 'archived'];
const TAB_STATUSES: Record<Tab, CampaignStatus[]> = {
  active: ['running', 'scheduled', 'paused'],
  inactive: ['sent', 'completed'],
  draft: ['draft'],
  archived: ['archived'],
};
const TAB_LABEL: Record<Tab, string> = {
  active: 'Aktivní',
  inactive: 'Dokončené',
  draft: 'Koncepty',
  archived: 'Archivované',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export interface MailingPageProps {
  space: MailingSpace;
  eyebrow: string;
  countries: readonly string[];
}

export function MailingKampanePage({ space, eyebrow, countries }: MailingPageProps) {
  const api = useMemo(() => mailingApi(space), [space]);
  const initial = new URLSearchParams(window.location.search);
  const [selected, setSelected] = useState<number | null>(() => {
    const p = initial.get('campaign');
    return p && !Number.isNaN(Number(p)) ? Number(p) : null;
  });
  const [creating, setCreating] = useState<boolean>(() => initial.get('new') === '1');
  const [fromTemplate] = useState<number | undefined>(() => {
    const t = Number(initial.get('template'));
    return Number.isInteger(t) && t > 0 ? t : undefined;
  });
  const [mode, setMode] = useState<EditorMode | undefined>(() => {
    const m = initial.get('editor');
    return m === 'builder' || m === 'editor' ? m : undefined;
  });
  const [choosing, setChoosing] = useState(false);
  const [checkingTemplate, setCheckingTemplate] = useState(() => initial.get('new') === '1' && !initial.get('editor'));

  useEffect(() => {
    if (!checkingTemplate) return;
    let cancelled = false;
    const done = (promo: boolean) => {
      if (cancelled) return;
      setCheckingTemplate(false);
      if (promo) { setMode('editor'); return; }
      setCreating(false);
      setChoosing(true);
    };
    if (!fromTemplate) { done(false); return; }
    api.template(fromTemplate).then((t) => done(isPromoTemplate(t.bodyHtml))).catch(() => done(false));
    return () => { cancelled = true; };
  }, [checkingTemplate, fromTemplate, api]);
  const [editing, setEditing] = useState<number | null>(() => {
    const p = initial.get('edit');
    return p && !Number.isNaN(Number(p)) ? Number(p) : null;
  });

  let body: ReactNode;
  if (checkingTemplate) {
    body = <div className="text-muted py-4">Načítám…</div>;
  } else if (creating) {
    body = <MailingCampaignWizard api={api} eyebrow={eyebrow} countries={countries} templateId={fromTemplate} mode={mode} onDone={() => { setCreating(false); setMode(undefined); syncParam('new', null); syncParam('template', null); syncParam('editor', null); }} />;
  } else if (editing != null) {
    body = <MailingCampaignWizard api={api} eyebrow={eyebrow} countries={countries} editId={editing} onDone={() => { setEditing(null); syncParam('edit', null); }} />;
  } else if (selected != null) {
    body = <CampaignDetailView api={api} id={selected} onBack={() => { setSelected(null); syncParam('campaign', null); }}
      onEdit={(eid) => { setSelected(null); syncParam('campaign', null); setEditing(eid); syncParam('edit', String(eid)); }} />;
  } else {
    body = (
      <>
        <CampaignListView
          api={api}
          eyebrow={eyebrow}
          countries={countries}
          onOpen={(id) => { setSelected(id); syncParam('campaign', String(id)); }}
          onNew={() => setChoosing(true)}
          onEdit={(id) => { setEditing(id); syncParam('edit', String(id)); }}
        />
        {choosing && (
          <MailingCampaignTypeChoice
            onClose={() => { setChoosing(false); syncParam('template', null); }}
            onPick={(m) => { setChoosing(false); setMode(m); setCreating(true); syncParam('new', '1'); syncParam('editor', m); if (fromTemplate) syncParam('template', String(fromTemplate)); }}
          />
        )}
      </>
    );
  }
  return <ArcPage as="div" className="p1-mailing">{body}</ArcPage>;
}

function syncParam(key: string, value: string | null) {
  const url = new URL(window.location.href);
  if (key === 'new') url.searchParams.delete('campaign');
  else if (key === 'campaign' || key === 'edit') url.searchParams.delete('new');
  if (value == null) url.searchParams.delete(key);
  else url.searchParams.set(key, value);
  window.history.replaceState({}, '', url.toString());
}

function StatusBadge({ status, pct }: { status: CampaignStatus; pct?: number }) {
  const m = STATUS_META[status];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 10px',
      borderRadius: 999, fontSize: '0.72rem', fontWeight: 500, background: m.bg, color: m.color,
    }}>{m.label}{pct != null ? ` ${pct} %` : ''}</span>
  );
}

function CampaignListView({ api, eyebrow, countries, onOpen, onNew, onEdit }: {
  api: ReturnType<typeof mailingApi>; eyebrow: string; countries: readonly string[];
  onOpen: (id: number) => void; onNew: () => void; onEdit: (id: number) => void;
}) {
  const [all, setAll] = useState<CampaignListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useTabParam<Tab>('tab', KAMPANE_TABS, 'active');
  const [fType, setFType] = useState('');
  const [fCountry, setFCountry] = useState('');
  const [fSource, setFSource] = useState('');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(50);
  const [compare, setCompare] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.campaigns()
      .then((d) => !cancelled && setAll(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [api]);

  const anyInFlight = !!all?.some((c) => c.status === 'running' || c.status === 'scheduled');
  useEffect(() => {
    if (!anyInFlight) return;
    const t = setInterval(() => { api.campaigns().then(setAll).catch(() => undefined); }, 5000);
    return () => clearInterval(t);
  }, [anyInFlight, api]);

  const counts = useMemo(() => {
    const c: Record<Tab, number> = { active: 0, inactive: 0, draft: 0, archived: 0 };
    if (all) for (const x of all) {
      for (const t of Object.keys(TAB_STATUSES) as Tab[]) if (TAB_STATUSES[t].includes(x.status)) c[t]++;
    }
    return c;
  }, [all]);

  const filtered = useMemo(() => {
    if (!all) return [];
    return all.filter((x) => {
      if (!TAB_STATUSES[tab].includes(x.status)) return false;
      if (fType && x.type !== fType) return false;
      if (fCountry && x.country !== fCountry) return false;
      if (fSource === INDIVIDUAL && x.templateName) return false;
      if (fSource && fSource !== INDIVIDUAL && x.templateName !== fSource) return false;
      if (search && !x.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [all, tab, fType, fCountry, fSource, search]);
  const templateNames = useMemo(() => [...new Set((all ?? []).map((x) => x.templateName).filter((n): n is string => !!n))].sort((a, b) => a.localeCompare(b, 'cs')), [all]);
  const rows = useMemo(() => (limit > 0 ? filtered.slice(0, limit) : filtered), [filtered, limit]);

  if (loading) return <div className="text-muted py-4">Načítám…</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <ArcPage className="rondo-campaign-list marketing-campaign-list">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow">{eyebrow}</div><h1>Kampaně</h1><p>Přehled e-mailových kampaní</p></div><Button type="button" onClick={onNew}><Icon name="plus" size={15} />Nová kampaň</Button></header>
      <ArcTabs
        label="Kampaně"
        value={tab}
        onChange={setTab}
        className="rondo-campaign-tabs"
        items={(Object.keys(TAB_STATUSES) as Tab[]).map((item) => ({
          value: item,
          label: <>{TAB_LABEL[item]} <span className="badge bg-secondary-subtle text-secondary ms-2">{counts[item]}</span></>,
        }))}
      />

      <ServiceBar
        className="marketing-campaign-servicebar"
        selects={[
          { label: 'Typ', value: fType, onChange: setFType, options: [{ value: '', label: 'Všechny' }, { value: 'oneshot', label: 'Jednorázová' }, { value: 'automated', label: 'Automatizovaná' }, { value: 'recurring', label: 'Opakovaná' }] },
          { label: 'Země', value: fCountry, onChange: setFCountry, options: [{ value: '', label: 'Všechny' }, ...countries.map((country) => ({ value: country, label: `${FLAG[country] ?? ''} ${country}`.trim() }))] },
          { label: 'Obsah', value: fSource, onChange: setFSource, options: [{ value: '', label: 'Všechny' }, { value: INDIVIDUAL, label: 'Individuální' }, ...templateNames.map((name) => ({ value: name, label: `Šablona: ${name}` }))] },
          { label: 'Limit', value: String(limit), onChange: (value) => setLimit(Number(value)), options: LIMITS.map((item) => ({ value: String(item), label: item === 0 ? 'Vše' : String(item) })) },
        ]}
        onReset={() => { setFType(''); setFCountry(''); setFSource(''); setSearch(''); }}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Hledat kampaň…"
        extra={<label className="service-switch"><input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} /><span>Srovnání</span></label>}
      />

      <div className="marketing-campaign-table-panel">
        <div className="table-responsive-mobile">
        <table className="marketing-campaign-table sticky-thead">
          <thead>
            <tr>
              <th>Kampaň</th><th>Trh a skupina</th><th>Stav</th>
              <th className="text-end">Příjemci</th><th className="text-end">Otevření</th><th className="text-end">Prokliky</th><th className="text-end">CTR</th>
              {compare && <th className="text-end">Δ CTR</th>}
              <th aria-label="Detail" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={compare ? 9 : 8} className="campaign-empty-row">
                <div className="campaign-empty-content"><span>Žádné kampaně.</span><Button type="button" variant="secondary" size="sm" onClick={onNew}><Icon name="plus" size={14} />Nová kampaň</Button></div>
              </td></tr>
            ) : rows.map((c, i) => {
                const prev = rows.slice(i + 1).find((p) => p.country === c.country && p.ctr != null);
                const delta = compare && c.ctr != null && prev?.ctr != null ? Math.round((c.ctr - prev.ctr) * 10) / 10 : null;
                return (
                <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(c.id)}>
                  <td><div className="campaign-name-cell"><span className={`campaign-mark type-${c.type}`}>{campaignInitials(c.name)}</span><span><strong>{c.name}</strong><small>{c.templateName ? `Šablona ${c.templateName}` : 'Individuální'} · {fmtDateTime(c.sentAt ?? c.scheduledAt ?? c.createdAt)}</small></span></div></td>
                  <td><div className="campaign-channel-cell">{c.country && <span>{FLAG[c.country] ?? ''} {c.country}</span>}<small>{c.segmentName ?? 'E-mail'}</small></div></td>
                  <td>
                    {c.status === 'scheduled' ? (
                      <div style={{ display: 'inline-block' }}>
                        <StatusBadge status={c.status} />
                        <div style={{ fontSize: 11, color: '#0369a1', marginTop: 2, whiteSpace: 'nowrap' }}>{c.scheduledAt ? fmtDateTime(c.scheduledAt) : 'ihned'}</div>
                      </div>
                    ) : (c.status === 'running' || c.status === 'paused') && c.recipients > 0 ? (() => {
                      const pct = Math.min(100, Math.round((c.jobsDone / c.recipients) * 100));
                      return (
                        <div style={{ display: 'inline-block' }}>
                          <StatusBadge status={c.status} pct={pct} />
                          <div style={{ height: 4, marginTop: 3, borderRadius: 2, background: '#e5e7eb', overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: 'var(--p1-green)', transition: 'width .4s' }} />
                          </div>
                        </div>
                      );
                    })() : <StatusBadge status={c.status} />}
                  </td>
                  <td className="text-end">{(c.recipients || c.recipientsEstimate || 0).toLocaleString('cs-CZ')}</td>
                  <td className="text-end">{c.opensUnique.toLocaleString('cs-CZ')}</td>
                  <td className="text-end">{c.uniqueClickers.toLocaleString('cs-CZ')}</td>
                  <td className="text-end">{c.ctr != null ? `${c.ctr} %` : '-'}</td>
                  {compare && <td className="text-end" style={{ color: delta == null ? '#9ca3af' : delta >= 0 ? '#137333' : '#c82b23' }}>{delta == null ? '-' : `${delta > 0 ? '+' : ''}${delta}`}</td>}
                  <td className="campaign-row-actions" onClick={(e) => e.stopPropagation()}>
                    <div>
                      <button type="button" className="btn-detail-row" title="Otevřít detail" onClick={() => onOpen(c.id)}>
                        <Icon name="chevron-right" size={16} />
                      </button>
                      {c.status === 'draft' && (
                        <button type="button" className="btn-detail-row" title="Upravit" onClick={() => onEdit(c.id)}>
                          <Icon name="edit-2" size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </ArcPage>
  );
}

const INDIVIDUAL = '__individual';

type DetailTab = 'prehledy' | 'udaje' | 'html' | 'prijemci' | 'prokliky';
const DETAIL_TABS: readonly DetailTab[] = ['prehledy', 'udaje', 'html', 'prijemci', 'prokliky'];

function CampaignDetailView({ api, id, onBack, onEdit }: { api: ReturnType<typeof mailingApi>; id: number; onBack: () => void; onEdit: (id: number) => void }) {
  const { confirm, dialog } = useConfirm();
  const [data, setData] = useState<CampaignDetail | null>(null);
  const [jobs, setJobs] = useState<CampaignJob[] | null>(null);
  const [clicks, setClicks] = useState<CampaignClick[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useTabParam<DetailTab>('ctab', DETAIL_TABS, 'prehledy');
  const [archiving, setArchiving] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  async function doArchive() {
    if (!data || archiving) return;
    if (!(await confirm({ title: 'Archivovat kampaň?', message: 'Kampaň zmizí z přehledu, najdeš ji v Archivovaných.', confirmLabel: 'Archivovat' }))) return;
    setArchiving(true);
    try { await api.archiveCampaign(data.id); onBack(); }
    catch (e) { setActionError(e instanceof ApiError ? e.message : 'Archivace selhala'); setArchiving(false); }
  }

  async function doToggleStatus(next: 'paused' | 'running') {
    if (!data || statusChanging) return;
    if (!(await confirm(next === 'paused' ? { title: 'Pozastavit kampaň?', message: 'Rozesílka se zastaví, pokračovat jde kdykoli později.', confirmLabel: 'Pozastavit' } : { title: 'Znovu spustit kampaň?', message: 'Rozesílka bude pokračovat u zbývajících příjemců.', confirmLabel: 'Spustit' }))) return;
    setStatusChanging(true);
    setActionError(null);
    try {
      await api.updateCampaign(data.id, { status: next });
      setData(await api.campaign(data.id));
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : 'Změna stavu selhala');
    } finally {
      setStatusChanging(false);
    }
  }

  async function doDuplicate() {
    if (!data || duplicating) return;
    setDuplicating(true);
    try { const { id: newId } = await api.duplicateCampaign(data.id); onEdit(newId); }
    catch (e) { setActionError(e instanceof ApiError ? e.message : 'Duplikace selhala'); setDuplicating(false); }
  }

  useEffect(() => {
    let cancelled = false;
    api.campaign(id)
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [id, api]);

  useEffect(() => {
    if (tab !== 'prijemci' || jobs) return;
    let cancelled = false;
    api.campaignJobs(id).then((j) => !cancelled && setJobs(j)).catch(() => undefined);
    return () => { cancelled = true; };
  }, [tab, id, jobs, api]);

  useEffect(() => {
    if (tab !== 'prokliky') return;
    let cancelled = false;
    api.campaignClicks(id).then((c) => !cancelled && setClicks(c)).catch(() => !cancelled && setClicks([]));
    return () => { cancelled = true; };
  }, [tab, id, api]);

  useEffect(() => {
    if (data?.status !== 'running' && data?.status !== 'scheduled') return;
    const t = setInterval(() => { api.campaign(id).then(setData).catch(() => undefined); }, 4000);
    return () => clearInterval(t);
  }, [data?.status, id, api]);

  if (loading) return <div className="text-muted py-4">Načítám…</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!data) return null;

  const scheduler = data.meta && typeof data.meta.scheduler_error === 'string' ? data.meta.scheduler_error : null;

  return (
    <ArcPage className="marketing-campaign-detail">
      <Button type="button" variant="secondary" size="sm" className="mailing-campaign-back" onClick={onBack}>Zpět na seznam</Button>
      {actionError && <div className="alert alert-danger py-2">{actionError}</div>}
      {data.status === 'paused' && scheduler && <div className="alert alert-warning py-2">Rozesílka se zastavila: {scheduler}</div>}

      <div className="card shadow-sm mb-3 marketing-campaign-identity"><div className="card-body d-flex align-items-center gap-3">
        <div style={{ width: 46, height: 46, borderRadius: 12, background: 'var(--p1-green-soft)', color: 'var(--p1-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name={(TYPE_META[data.type]?.icon) ?? 'mail'} size={22} />
        </div>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h5 className="mb-0">{data.name}</h5>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
            {data.country && <span className="badge bg-light text-dark border">{FLAG[data.country] ?? ''} {data.country}</span>}
            <span className="badge bg-light text-dark border">{TYPE_META[data.type]?.label ?? data.type}</span>
            <span className="badge bg-light text-dark border d-inline-flex align-items-center gap-1"><Icon name="mail" size={12} />mail</span>
            {data.subject && <span className="text-muted small">{data.subject}</span>}
          </div>
        </div>
        <div className="d-flex flex-column align-items-end gap-2 flex-shrink-0 ms-3">
          <div className="d-flex align-items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
            {data.sentAt && <span className="text-muted small">Odesláno {fmtDateTime(data.sentAt)}</span>}
            <StatusBadge status={data.status} />
          </div>
          <div className="d-flex align-items-center gap-2 flex-nowrap">
            {data.status === 'draft' && (
              <Button type="button" variant="secondary" size="sm" onClick={() => onEdit(data.id)}>
                <Icon name="edit-2" size={13} /> Upravit
              </Button>
            )}
            {(data.status === 'running' || data.status === 'scheduled') && (
              <Button type="button" variant="secondary" size="sm" onClick={() => doToggleStatus('paused')} loading={statusChanging}>
                <Icon name="pause" size={13} /> {statusChanging ? 'Měním…' : 'Pozastavit'}
              </Button>
            )}
            {data.status === 'paused' && (
              <Button type="button" variant="secondary" size="sm" onClick={() => doToggleStatus('running')} loading={statusChanging}>
                <Icon name="play" size={13} /> {statusChanging ? 'Měním…' : 'Znovu spustit'}
              </Button>
            )}
            <Button type="button" variant="secondary" size="sm" onClick={doDuplicate} loading={duplicating}>
              <Icon name="copy" size={13} /> {duplicating ? 'Duplikuji…' : 'Duplikovat'}
            </Button>
            {data.status !== 'archived' && <Button type="button" variant="danger" size="sm" onClick={doArchive} loading={archiving}>Archivovat</Button>}
          </div>
        </div>
      </div></div>

      {(data.status === 'running' || data.status === 'scheduled' || data.status === 'paused') && (() => {
        const total = data.recipients || 0;
        const done = data.jobStates.sent + data.jobStates.failed + data.jobStates.skipped;
        const pct = total ? Math.round((done / total) * 100) : 0;
        const running = data.status === 'running';
        return (
          <div className="card shadow-sm mb-3"><div className="card-body py-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold">
                {data.status === 'scheduled'
                  ? <>Naplánováno na <span style={{ color: '#0369a1' }}>{data.scheduledAt ? fmtDateTime(data.scheduledAt) : 'ihned'}</span></>
                  : data.status === 'paused' ? 'Pozastaveno' : 'Odesílá se…'}
              </span>
              <span className="text-muted small">{data.jobStates.sent} / {total} odesláno ({pct} %)</span>
            </div>
            <div className="progress" style={{ height: 10 }}>
              <div className={`progress-bar${running ? ' progress-bar-striped progress-bar-animated' : ''}`}
                role="progressbar" style={{ width: `${pct}%`, background: running ? 'var(--p1-green)' : undefined }}
                aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} />
            </div>
            {(data.jobStates.failed > 0 || data.jobStates.skipped > 0) && (
              <div className="text-muted small mt-1">
                {data.jobStates.skipped > 0 && <>přeskočeno {data.jobStates.skipped}</>}
                {data.jobStates.failed > 0 && <span className="text-danger"> · chyb {data.jobStates.failed}</span>}
              </div>
            )}
          </div></div>
        );
      })()}

      <ArcTabs
        label="Detail kampaně"
        value={tab}
        onChange={setTab}
        className="marketing-detail-tabs"
        items={DETAIL_TABS.map((item) => ({
          value: item,
          label: { prehledy: 'Přehledy', udaje: 'Údaje', html: 'HTML obsah', prijemci: `Příjemci (${data.recipients})`, prokliky: `Prokliky (${data.clicksTotal})` }[item],
        }))}
      />

      {tab === 'prehledy' && <CampaignAnalytics data={data} />}

      {tab === 'udaje' && (
        <div className="card shadow-sm"><div className="card-body">
          <div className="udaje-grid">
            <div className="udaje-section-title">Identifikace</div>
            <UdajeRow label="ID" value={<code>#{data.id}</code>} />
            <UdajeRow label="Název" value={data.name} />
            <UdajeRow label="Typ" value={TYPE_META[data.type]?.label ?? data.type} />
            <UdajeRow label="Stav" value={STATUS_META[data.status].label} />
            <UdajeRow label="Kanál" value="E-mail" />

            <div className="udaje-section-title">Cílení</div>
            <UdajeRow label="Trh" value={data.country ? `${FLAG[data.country] ?? ''} ${data.country}`.trim() : undefined} />
            <UdajeRow label="Skupina" value={data.segmentName} />
            <UdajeRow label="Předmět" value={data.subject} />
            <UdajeRow label="Obsah" value={data.templateName ? `Šablona ${data.templateName}` : 'Individuální'} />

            <div className="udaje-section-title">Termíny</div>
            <UdajeRow label="Naplánováno" value={fmtDateTime(data.scheduledAt)} />
            <UdajeRow label="Odesláno" value={fmtDateTime(data.sentAt)} />
            <UdajeRow label="Vytvořeno" value={fmtDateTime(data.createdAt)} />
          </div>
        </div></div>
      )}

      {tab === 'html' && (
        <div className="card shadow-sm"><div className="card-body">
          {data.htmlBody
            ? <iframe title="Náhled HTML" sandbox="allow-same-origin" srcDoc={sampleVars(data.htmlBody)} style={{ width: '100%', height: 600, border: '1px solid #eef0f3', borderRadius: 8 }} />
            : <div className="text-muted">Žádný HTML obsah.</div>}
        </div></div>
      )}

      {tab === 'prokliky' && <CampaignClicks clicks={clicks} />}

      {tab === 'prijemci' && (
        <div>
          <div className="table-responsive-mobile">
          <table className="table table-sm align-middle sticky-thead" style={{ fontSize: '0.82rem' }}>
            <thead><tr className="text-muted"><th>Kontakt</th><th>Adresát</th><th>Stav</th><th className="text-end">Pokusy</th><th>Odesláno</th><th>Doručeno</th><th className="text-end">Otevření</th><th className="text-end">Prokliky</th><th>Chyba</th></tr></thead>
            <tbody>
              {(jobs ?? []).map((j) => (
                <tr key={j.id}><td>{j.recipientName ?? '-'}</td><td>{j.recipientAddress}</td><td>{JOB_STATE[j.state] ?? j.state}</td>
                  <td className="text-end">{j.attempts}</td><td>{fmtDate(j.sentAt)}</td><td>{fmtDate(j.deliveredAt)}</td>
                  <td className="text-end">{j.openCount}</td><td className="text-end">{j.clickCount}</td>
                  <td className="text-danger small">{j.lastError ?? ''}</td></tr>
              ))}
              {jobs && jobs.length === 0 && <tr><td colSpan={9} className="text-muted text-center py-3">Zatím žádní příjemci.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}
      {dialog}
    </ArcPage>
  );
}

const SOURCE_LABEL: Record<string, string> = { affiliate: 'Affiliate', inzerent: 'Klient', manual: 'Ruční' };

function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = `${u.pathname}${u.search}`;
    return `${u.hostname}${path.length > 60 ? `${path.slice(0, 57)}…` : path}`;
  } catch {
    return url;
  }
}

function CampaignClicks({ clicks }: { clicks: CampaignClick[] | null }) {
  if (!clicks) return <div className="text-muted py-3">Načítám…</div>;
  const byUrl = new Map<string, { total: number; people: Set<string> }>();
  for (const c of clicks) {
    const key = c.url ?? '-';
    const e = byUrl.get(key) ?? { total: 0, people: new Set<string>() };
    e.total++;
    if (c.email) e.people.add(c.email.toLowerCase());
    byUrl.set(key, e);
  }
  const summary = [...byUrl.entries()].sort((a, b) => b[1].total - a[1].total);
  return (
    <div className="campaign-clicks">
      {summary.length > 0 && (
        <div className="campaign-click-summary">
          {summary.map(([url, s]) => (
            <div key={url}><a href={url} target="_blank" rel="noopener noreferrer" title={url}>{shortUrl(url)}</a><span><strong>{s.total}</strong> prokliků · {s.people.size} lidí</span></div>
          ))}
        </div>
      )}
      <div className="table-responsive-mobile">
        <table className="table table-sm align-middle sticky-thead" style={{ fontSize: '0.82rem' }}>
          <thead><tr className="text-muted"><th>Čas</th><th>Kontakt</th><th>Affiliate / klient</th><th>Odkaz</th></tr></thead>
          <tbody>
            {clicks.map((c) => (
              <tr key={c.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(c.occurredAt)}</td>
                <td><strong>{c.name ?? '-'}</strong><div className="text-muted small">{c.email ?? '-'}</div></td>
                <td>{c.parentName ?? '-'}{c.source && <div className="text-muted small">{SOURCE_LABEL[c.source] ?? c.source}</div>}</td>
                <td>{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" title={c.url}>{shortUrl(c.url)}</a> : '-'}</td>
              </tr>
            ))}
            {clicks.length === 0 && <tr><td colSpan={4} className="text-muted text-center py-3">Zatím žádné prokliky.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CampaignAnalytics({ data }: { data: CampaignDetail }) {
  const sent = data.jobStates.sent || data.recipients;
  const stages = [
    { label: 'Příjemci', value: data.recipients, tone: 'ink' },
    { label: 'Odesláno', value: sent, tone: 'green' },
    { label: 'Doručeno', value: data.deliveredTotal, tone: 'sage' },
    { label: 'Otevřeno', value: data.opensUnique, tone: 'orange' },
    { label: 'Kliknuto', value: data.uniqueClickers, tone: 'violet' },
  ];
  const max = Math.max(...stages.map((stage) => stage.value), 1);
  const openRate = data.deliveredTotal ? Math.min(100, (data.opensUnique / data.deliveredTotal) * 100) : 0;
  const deliveryRate = data.recipients ? Math.min(100, (data.deliveredTotal / data.recipients) * 100) : 0;
  const bounceRate = data.recipients ? Math.min(100, (data.bouncesTotal / data.recipients) * 100) : 0;
  const metrics = [
    { label: 'Doručitelnost', value: `${deliveryRate.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} %`, note: `${data.deliveredTotal.toLocaleString('cs-CZ')} doručeno`, icon: 'check-circle' },
    { label: 'Open rate', value: `${openRate.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} %`, note: `${data.opensUnique.toLocaleString('cs-CZ')} unikátních`, icon: 'mail' },
    { label: 'CTR', value: `${data.ctr ?? 0} %`, note: `${data.uniqueClickers.toLocaleString('cs-CZ')} klikajících`, icon: 'mouse-pointer' },
  ];
  return (
    <div className="campaign-analytics">
      <div className="campaign-analytics-head"><div><span>Výkon kampaně</span><h2>Cesta od odeslání ke kliknutí</h2></div><p>{data.sentAt ? `Data od ${fmtDateTime(data.sentAt)}` : 'Průběžná data kampaně'}</p></div>
      <div className="campaign-analytics-layout">
        <section className="campaign-funnel" aria-label="Výkonnostní cesta kampaně">
          <div className="campaign-funnel-summary"><div><span>Hlavní výsledek</span><strong>{data.deliveredTotal.toLocaleString('cs-CZ')}</strong><small>doručených zpráv</small></div><Icon name="send" size={24} /></div>
          <div className="campaign-funnel-chart">
            {stages.map((stage) => {
              const pct = stage.value > 0 ? Math.max(3, (stage.value / max) * 100) : 0;
              return <div className="campaign-funnel-row" key={stage.label}><div><span>{stage.label}</span><strong>{stage.value.toLocaleString('cs-CZ')}</strong></div><div className="campaign-funnel-track"><i className={`tone-${stage.tone}`} style={{ width: `${pct}%` }} /></div><small>{Math.round((stage.value / max) * 100)} %</small></div>;
            })}
          </div>
        </section>
        <aside className="campaign-rate-panel">
          {metrics.map((metric) => <div className="campaign-rate" key={metric.label}><span className="campaign-rate-icon"><Icon name={metric.icon} size={18} /></span><div><small>{metric.label}</small><strong>{metric.value}</strong><span>{metric.note}</span></div></div>)}
          <div className="campaign-quality"><span>Kvalita rozesílky</span><div><i style={{ width: `${Math.max(0, 100 - bounceRate)}%` }} /></div><small>{data.bouncesTotal.toLocaleString('cs-CZ')} nedoručeno · {data.unsubscribesTotal.toLocaleString('cs-CZ')} odhlášení · {data.spamReportsTotal.toLocaleString('cs-CZ')} spam</small></div>
        </aside>
      </div>
    </div>
  );
}

function UdajeRow({ label, value }: { label: string; value: ReactNode }) {
  const empty = value == null || value === '' || value === '-';
  return (
    <div className="udaje-row">
      <div className="udaje-label">{label}</div>
      <div className="udaje-value">{empty ? <span className="muted">-</span> : value}</div>
    </div>
  );
}
